import { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { categoryAPI, departmentAPI } from "../services/api";
import { toast } from "sonner";
import { FolderOpen, Search, Plus, Edit, X, Save, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

interface Category {
  id: string;
  name: string;
  description: string | null;
  departmentId: string | null;
  isActive: boolean;
  createdAt: string;
  department?: { id: string; name: string };
}

interface Department {
  id: string;
  name: string;
}

export default function CategoryManagement() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    departmentId: "",
    isActive: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, deptRes] = await Promise.all([
        categoryAPI.list(),
        departmentAPI.list(),
      ]);

      if (categoriesRes.success || Array.isArray(categoriesRes)) {
        const catList = Array.isArray(categoriesRes)
          ? categoriesRes
          : categoriesRes.data?.categories ||
            categoriesRes.data?.items ||
            categoriesRes.data ||
            [];
        setCategories(Array.isArray(catList) ? catList : []);
      } else {
        setCategories([]);
      }

      if (deptRes.success || Array.isArray(deptRes)) {
        const deptList = Array.isArray(deptRes)
          ? deptRes
          : deptRes.data?.departments ||
            deptRes.data?.items ||
            deptRes.data ||
            [];
        setDepartments(Array.isArray(deptList) ? deptList : []);
      } else {
        setDepartments([]);
      }
    } catch (error) {
      toast.error("Failed to load data");
      setCategories([]);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setSelectedCategory(null);
    setFormData({
      name: "",
      description: "",
      departmentId: "",
      isActive: true,
    });
    setShowDialog(true);
  };

  const openEditDialog = (category: Category) => {
    setSelectedCategory(category);
    setFormData({
      name: category.name,
      description: category.description || "",
      departmentId: category.departmentId || "",
      isActive: category.isActive,
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload: Record<string, any> = {
        name: formData.name,
        description: formData.description || null,
        isActive: formData.isActive,
      };

      if (formData.departmentId) {
        payload.departmentId = formData.departmentId;
      }

      if (selectedCategory) {
        const response = await categoryAPI.update(selectedCategory.id, payload);

        if (response.success) {
          toast.success("Category updated successfully");
          loadData();
          setShowDialog(false);
        }
      } else {
        const response = await categoryAPI.create(payload);

        if (response.success) {
          toast.success("Category created successfully");
          loadData();
          setShowDialog(false);
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to save category");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDepartmentName = (deptId: string | null) => {
    if (!deptId) return "No Department";
    const dept = departments.find((d) => d.id === deptId);
    return dept?.name || "Unknown";
  };

  const filteredCategories = Array.isArray(categories)
    ? categories.filter((cat) =>
        (cat.name || "").toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : [];

  const stats = {
    total: Array.isArray(categories) ? categories.length : 0,
    active: Array.isArray(categories)
      ? categories.filter((c) => c.isActive).length
      : 0,
  };

  return (
    <DashboardLayout>
      <div className="min-h-full p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
              Category Management
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage ticket categories and classifications
            </p>
          </motion.div>

          <button
            onClick={openCreateDialog}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all hover:scale-105 shadow-lg cursor-pointer"
          >
            <Plus className="w-5 h-5" />
            Add Category
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            {
              label: "Total Categories",
              value: stats.total,
              color: "text-primary",
            },
            { label: "Active", value: stats.active, color: "text-green-400" },
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-card border border-border/50 rounded-xl p-4 shadow-lg"
            >
              <p className="text-xs text-muted-foreground mb-1">{stat.label}</p>
              <p className={`text-2xl font-bold ${stat.color} font-mono`}>
                {stat.value}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border/50 rounded-xl p-4 shadow-lg"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="search"
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
            />
          </div>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center py-12">
            <FolderOpen className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No categories found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCategories.map((category, idx) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-card border border-border/50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <FolderOpen className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-foreground truncate">
                        {category.name}
                      </h3>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        <span
                          className={`text-xs px-2 py-0.5 rounded ${
                            category.isActive
                              ? "bg-green-500/10 text-green-400"
                              : "bg-gray-500/10 text-gray-400"
                          }`}
                        >
                          {category.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {category.description && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {category.description}
                  </p>
                )}

                <div className="mb-4 pb-3 border-b border-border/30">
                  <p className="text-xs text-muted-foreground">Department</p>
                  <p className="text-sm font-medium text-foreground">
                    {category.department?.name ||
                      getDepartmentName(category.departmentId)}
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => openEditDialog(category)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg transition-colors text-sm font-medium cursor-pointer"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {showDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-card border border-border/50 rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-foreground">
                  {selectedCategory ? "Edit Category" : "Create New Category"}
                </h3>
                <button
                  onClick={() => setShowDialog(false)}
                  className="p-2 hover:bg-muted rounded-lg transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={3}
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Department
                  </label>
                  <select
                    value={formData.departmentId}
                    onChange={(e) =>
                      setFormData({ ...formData, departmentId: e.target.value })
                    }
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                  >
                    <option value="">No Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                    className="w-4 h-4 text-primary bg-muted border-border rounded focus:ring-2 focus:ring-primary/50"
                  />
                  <label
                    htmlFor="isActive"
                    className="text-sm font-medium text-foreground cursor-pointer"
                  >
                    Active Category
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowDialog(false)}
                    className="flex-1 px-4 py-2.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all font-medium cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all disabled:opacity-50 cursor-pointer"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        {selectedCategory ? "Update" : "Create"} Category
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
